/**
 * EditGroupDrawer — edita nome, ícone, cor e participantes de um grupo do chat Web.
 * Segue o padrão dos drawers do sistema: header com a cor de destaque, corpo em
 * blocos e footer com as ações. Quem é removido fica em modo leitura.
 */
import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, LogOut, Plus, Search, UserMinus, Users, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "../ui/sonner";
import IconPicker from "../common/IconPicker";
import api, { formatApiError } from "../../utils/api";
import { accentBg } from "../../utils/accent";
import { GROUP_COLORS, groupColors, groupIcon } from "./groupIcons";

const Avatar = ({ name, muted }) => (
  <span
    className={`h-8 w-8 rounded-full text-[12px] font-semibold text-white flex items-center justify-center shrink-0 ${
      muted ? "opacity-40" : ""
    }`}
    style={accentBg()}
  >
    {(name || "?").slice(0, 1).toUpperCase()}
  </span>
);

export default function EditGroupDrawer({ open, onClose, thread, webUsers = [], meId, onChanged }) {
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState("Users");
  const [color, setColor] = useState("accent");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(thread?.title || "");
      setIcon(thread?.icon || "Users");
      setColor(thread?.color || "accent");
      setSearch("");
    }
  }, [open, thread?.id, thread?.title, thread?.icon, thread?.color]);

  const participants = thread?.participants || [];
  const ativos = participants.filter((p) => !p.removed);
  const removidos = participants.filter((p) => p.removed);
  const idsAtivos = useMemo(() => new Set(ativos.map((p) => p.id)), [ativos]);

  const candidatos = useMemo(() => {
    const q = search.trim().toLowerCase();
    return webUsers
      .filter((u) => !idsAtivos.has(u.id) && !removidos.some((r) => r.id === u.id))
      .filter((u) => (q ? (u.name || "").toLowerCase().includes(q) : true));
  }, [webUsers, idsAtivos, removidos, search]);

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      await onChanged?.();
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error("Não foi possível concluir", {
        description: formatApiError(err.response?.data?.detail),
      });
    } finally {
      setBusy(false);
    }
  };

  const saveIdentity = () =>
    call(
      () =>
        api.patch(`/chat/internal/threads/${thread.id}`, {
          title: title.trim(),
          icon,
          color,
        }),
      "Grupo atualizado",
    );

  const addUser = (id) =>
    call(
      () =>
        api.post(`/chat/internal/threads/${thread.id}/participants`, {
          participant_ids: [id],
        }),
      "Participante adicionado",
    );

  const removeUser = (id) =>
    call(async () => {
      await api.delete(`/chat/internal/threads/${thread.id}/participants/${id}`);
      if (id === meId) {
        onClose?.();
        toast.success("Você saiu do grupo", {
          description:
            ativos.length <= 1
              ? "Sem participantes ativos, o grupo foi arquivado (só leitura)."
              : "A conversa fica disponível apenas para leitura.",
        });
      } else {
        toast.success("Participante removido");
      }
    });

  const leave = () => {
    if (ativos.length <= 1) {
      setConfirmLeave(true);
      return;
    }
    removeUser(meId);
  };

  if (!thread) return null;
  const identityChanged =
    (title.trim() || "") !== (thread.title || "") ||
    icon !== (thread.icon || "Users") ||
    color !== (thread.color || "accent");
  const minimo = ativos.length <= 2;
  const badge = groupColors(color);
  const BadgeIcon = groupIcon(icon);
  const souParticipante = ativos.some((p) => p.id === meId);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:w-[32.5rem] sm:max-w-none flex flex-col gap-0 p-0 !z-[1800] [&>button:first-child]:hidden"
        overlayClassName="!z-[1799]"
        data-testid="edit-group-drawer"
      >
        {/* Header */}
        <div
          className="px-6 py-4 text-white flex items-center justify-between shrink-0"
          style={accentBg()}
        >
          <SheetTitle data-testid="sheet-title" className="flex items-center gap-2.5 text-white">
            <Users className="h-5 w-5" />
            Editar grupo
          </SheetTitle>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors cursor-pointer"
            data-testid="sheet-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Resumo */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 shrink-0 flex items-center gap-4">
          <span
            className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: badge.bg }}
            data-testid="edit-group-preview"
          >
            <BadgeIcon size={22} style={{ color: badge.fg }} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {title.trim() || `${ativos.length} pessoas`}
            </p>
            <p className="text-xs text-gray-500">
              {ativos.length} participante{ativos.length === 1 ? "" : "s"} ativo
              {ativos.length === 1 ? "" : "s"}
              {removidos.length ? ` · ${removidos.length} em leitura` : ""}
            </p>
          </div>
        </div>

        {/* Corpo */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {thread.status === "archived" && (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-800"
              data-testid="edit-group-archived-note"
            >
              Grupo arquivado por ficar sem participantes. Adicione alguém para reativar a
              conversa.
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-gray-600">Nome do grupo</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={60}
                placeholder="Ex.: Plantão noturno"
                className="h-9 text-sm mt-1.5"
                data-testid="edit-group-title-input"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-600">Ícone</Label>
              <div className="mt-1.5">
                <IconPicker
                  value={icon}
                  onChange={setIcon}
                  defaultIcon="Users"
                  contentClassName="!z-[1900]"
                  overlayClassName="!z-[1899]"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-600">Cor do ícone</Label>
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="edit-group-colors">
                {Object.entries(GROUP_COLORS).map(([key, { hex, label }]) => {
                  const on = (color || "accent") === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      title={label}
                      onClick={() => setColor(key)}
                      className="h-8 w-8 rounded-full flex items-center justify-center border-2 transition-transform hover:scale-105"
                      style={{ borderColor: on ? "#0f172a" : "transparent" }}
                      data-testid={`edit-group-color-${key}`}
                    >
                      <span
                        className="h-6 w-6 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: hex || "var(--accent)" }}
                      >
                        {on && <Check size={13} className="text-white" strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-gray-600">Participantes</Label>
              <span className="text-[11px] text-gray-400">{ativos.length} ativos</span>
            </div>
            <div
              className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden"
              data-testid="edit-group-participants"
            >
              {ativos.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2.5 text-[13px] flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <Avatar name={p.name} />
                  <span className="flex-1 truncate text-gray-800">
                    {p.name}
                    {p.id === meId && <span className="text-gray-400"> · você</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => (p.id === meId ? leave() : removeUser(p.id))}
                    disabled={busy || (p.id !== meId && minimo)}
                    title={
                      p.id === meId
                        ? "Sair do grupo"
                        : minimo
                          ? "Um grupo precisa de pelo menos 2 participantes"
                          : "Remover do grupo"
                    }
                    className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400 cursor-pointer"
                    data-testid={`edit-group-remove-${p.id}`}
                  >
                    <UserMinus size={16} />
                  </button>
                </div>
              ))}
              {removidos.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2.5 text-[13px] flex items-center gap-3 bg-gray-50/60"
                >
                  <Avatar name={p.name} muted />
                  <span className="flex-1 truncate text-gray-400">
                    {p.name}
                    <span className="text-[10px] uppercase tracking-wide"> · só leitura</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => addUser(p.id)}
                    disabled={busy}
                    title="Adicionar de volta"
                    className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                    data-testid={`edit-group-readd-${p.id}`}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-gray-600">Adicionar pessoas</Label>
            <div className="relative mt-1.5">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar pessoa…"
                className="pl-8 h-9 text-sm"
                data-testid="edit-group-search"
              />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
              {candidatos.length === 0 ? (
                <p className="text-[12px] text-gray-400 px-3 py-6 text-center">
                  Nenhum outro usuário web disponível.
                </p>
              ) : (
                candidatos.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => addUser(u.id)}
                    disabled={busy}
                    className="w-full text-left px-3 py-2.5 text-[13px] flex items-center gap-3 hover:bg-gray-50 transition-colors disabled:opacity-50 cursor-pointer"
                    data-testid={`edit-group-add-${u.id}`}
                  >
                    <Avatar name={u.name} />
                    <span className="flex-1 truncate text-gray-800">{u.name}</span>
                    <Plus size={16} style={{ color: "var(--accent)" }} className="shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white shrink-0 flex items-center justify-between gap-2">
          {souParticipante ? (
            <Button
              variant="outline"
              onClick={leave}
              disabled={busy}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 cursor-pointer"
              data-testid="edit-group-leave"
            >
              <LogOut size={14} />
              Sair do grupo
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="cursor-pointer" data-testid="edit-group-close">
              Fechar
            </Button>
            <Button
              onClick={saveIdentity}
              disabled={busy || !identityChanged}
              className="text-white cursor-pointer"
              style={accentBg()}
              data-testid="edit-group-save-title"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={15} />}
              Salvar
            </Button>
          </div>
        </div>
      </SheetContent>

      <AlertDialog open={confirmLeave} onOpenChange={setConfirmLeave}>
        <AlertDialogContent className="!z-[1900]" overlayClassName="!z-[1899]">
          <AlertDialogHeader>
            <AlertDialogTitle>Sair e arquivar o grupo?</AlertDialogTitle>
            <AlertDialogDescription>
              Você é a última pessoa ativa. Ao sair, o grupo é arquivado e fica só para
              leitura — o histórico continua salvo e ele volta a ativo se alguém for
              adicionado depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-leave-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmLeave(false);
                removeUser(meId);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-leave-ok"
            >
              Sair e arquivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
